/**
 * Link-quality slice — ingests DataChannel `link-quality` messages into a latest
 * snapshot + short history for the control instance.
 *
 * Framework-free. Reuses the shared pure link-level helpers (normalizeLevel/HISTORY_LEN)
 * from `./level-scale` for the 5-level scale mapping.
 *
 * Not to be confused with `connection-quality.ts` in this same module: that one scores
 * the LOCAL WebRTC transport (RTT/loss measured by the browser); this one ingests
 * quality numbers the ROBOT itself reports over its uplink to the SFU. Same scale,
 * different signal source — see the module README.
 */

import { createStore } from "zustand/vanilla";
import type { StoreApi } from "zustand/vanilla";

import { HISTORY_LEN, normalizeLevel, resolveMutedLevel } from "./level-scale";
import type { ResolvedLinkLevel } from "./level-scale";

export interface LinkQualityPayload {
  schema_version?: string;
  timestamp?: number;
  leg?: string;
  interval_ms?: number;
  active_sources?: string[];
  rtt_ms?: number | null;
  loss_pct?: number | null;
  transport_state?: string;
  level?: ResolvedLinkLevel;
  abr_profile?: string | null;
  abr_fps?: number | null;
  abr_bitrate_kbps?: number | null;
  receivedAt?: number;
}

export interface LinkQualitySlice extends Pick<
  StoreApi<LinkQualityPayload | null>,
  "getState" | "getInitialState" | "subscribe"
> {
  /** last N snapshots (oldest → newest) */
  getHistory(): LinkQualityPayload[];
  /** ms since last link-quality packet */
  getAgeMs(): number | null;
  ingest(message: unknown): void;
  reset(): void;
}

export function createLinkQualitySlice(): LinkQualitySlice {
  let lastAt: number | null = null;
  let history: LinkQualityPayload[] = [];
  const store = createStore<LinkQualityPayload | null>(() => null);

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,
    getHistory: () => history.slice(),
    getAgeMs: () => (lastAt == null ? null : Date.now() - lastAt),
    ingest(message) {
      let raw: unknown = message;
      if (typeof raw !== "string") {
        if (raw instanceof ArrayBuffer) raw = new TextDecoder().decode(new Uint8Array(raw));
        else if (ArrayBuffer.isView(raw)) raw = new TextDecoder().decode(raw);
        else raw = String(raw);
      }
      try {
        const parsed = JSON.parse(String(raw).trim()) as Record<string, unknown>;
        const event = parsed.event ?? parsed.type;
        if (event !== "link-quality") {
          return;
        }
        const payload = parsed.payload ?? parsed.data;
        if (!payload || typeof payload !== "object") return;
        const rec = payload as Record<string, unknown>;
        lastAt = Date.now();
        const normalized = resolveMutedLevel(
          normalizeLevel(rec.level as string | null | undefined)
        );
        const abrProfile = rec.abr_profile ?? rec.profile ?? rec.abrProfile ?? null;
        const abrFps = rec.abr_fps ?? rec.fps ?? rec.abrFps ?? null;
        const abrKbps = rec.abr_bitrate_kbps ?? rec.bitrate_kbps ?? rec.abrBitrateKbps ?? null;
        const snapshot: LinkQualityPayload = {
          schema_version: typeof rec.schema_version === "string" ? rec.schema_version : undefined,
          timestamp: typeof rec.timestamp === "number" ? rec.timestamp : undefined,
          leg: typeof rec.leg === "string" ? rec.leg : undefined,
          interval_ms: typeof rec.interval_ms === "number" ? rec.interval_ms : undefined,
          active_sources: Array.isArray(rec.active_sources) ? rec.active_sources : [],
          rtt_ms: (rec.rtt_ms as number | null | undefined) ?? null,
          loss_pct: (rec.loss_pct as number | null | undefined) ?? null,
          transport_state:
            typeof rec.transport_state === "string" ? rec.transport_state : undefined,
          level: normalized,
          abr_profile:
            abrProfile != null && String(abrProfile).trim()
              ? String(abrProfile).trim().toLowerCase()
              : null,
          abr_fps: abrFps != null && Number.isFinite(Number(abrFps)) ? Number(abrFps) : null,
          abr_bitrate_kbps:
            abrKbps != null && Number.isFinite(Number(abrKbps)) ? Number(abrKbps) : null,
          receivedAt: lastAt,
        };
        history = [...history, snapshot].slice(-HISTORY_LEN);
        store.setState(snapshot, true);
      } catch {
        /* not JSON or unrelated */
      }
    },
    reset() {
      lastAt = null;
      history = [];
      store.setState(null, true);
    },
  };
}
